import {
  addDoc,
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  where,
  type Timestamp,
} from 'firebase/firestore';
import { appConfig } from '@/config/app';
import { AUDIT_LOGS_COLLECTION } from '@/services/audit/constants';
import { getFirestoreDb } from '@/services/firebase';
import type {
  AuditAction,
  AuditEntityType,
  AuditLog,
  AuditLogFilters,
  AuditLogInput,
} from '@/types/audit';

type AuditLogDoc = {
  companyId: string;
  action: AuditAction;
  entityType: AuditEntityType;
  entityId: string;
  actorUid: string;
  actorEmail?: string;
  actorRole?: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  changedFields?: string[];
  metadata?: Record<string, unknown>;
  createdAt: Timestamp;
};

function mapAuditLog(id: string, data: AuditLogDoc): AuditLog {
  return {
    id,
    companyId: data.companyId,
    action: data.action,
    entityType: data.entityType,
    entityId: data.entityId,
    actorUid: data.actorUid,
    ...(data.actorEmail !== undefined ? { actorEmail: data.actorEmail } : {}),
    ...(data.actorRole !== undefined ? { actorRole: data.actorRole } : {}),
    ...(data.before !== undefined ? { before: data.before } : {}),
    ...(data.after !== undefined ? { after: data.after } : {}),
    ...(data.changedFields !== undefined
      ? { changedFields: data.changedFields }
      : {}),
    ...(data.metadata !== undefined ? { metadata: data.metadata } : {}),
    createdAt: data.createdAt.toDate(),
  };
}

export async function appendAuditLog(
  input: AuditLogInput,
  actor: { uid: string; email: string; role: string; companyId: string },
): Promise<void> {
  const payload = {
    companyId: actor.companyId,
    action: input.action,
    entityType: input.entityType,
    entityId: input.entityId,
    actorUid: actor.uid,
    actorEmail: actor.email,
    actorRole: actor.role,
    ...(input.before !== undefined ? { before: input.before } : {}),
    ...(input.after !== undefined ? { after: input.after } : {}),
    ...(input.changedFields !== undefined
      ? { changedFields: input.changedFields }
      : {}),
    ...(input.metadata !== undefined ? { metadata: input.metadata } : {}),
    createdAt: serverTimestamp(),
  };

  await addDoc(collection(getFirestoreDb(), AUDIT_LOGS_COLLECTION), payload);
}

export async function listAuditLogs(
  filters: AuditLogFilters = {},
  companyId: string = appConfig.companyId,
): Promise<AuditLog[]> {
  const max = filters.limit ?? 200;
  const constraints = [
    where('companyId', '==', companyId),
    orderBy('createdAt', 'desc'),
    limit(max),
  ];

  if (filters.entityType && filters.entityId) {
    const q = query(
      collection(getFirestoreDb(), AUDIT_LOGS_COLLECTION),
      where('companyId', '==', companyId),
      where('entityType', '==', filters.entityType),
      where('entityId', '==', filters.entityId),
      orderBy('createdAt', 'desc'),
      limit(max),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => mapAuditLog(d.id, d.data() as AuditLogDoc));
  }

  const q = query(
    collection(getFirestoreDb(), AUDIT_LOGS_COLLECTION),
    ...constraints,
  );
  const snap = await getDocs(q);
  let logs = snap.docs.map((d) => mapAuditLog(d.id, d.data() as AuditLogDoc));

  if (filters.entityType) {
    logs = logs.filter((l) => l.entityType === filters.entityType);
  }
  if (filters.entityId) {
    logs = logs.filter((l) => l.entityId === filters.entityId);
  }
  if (filters.actorUid) {
    logs = logs.filter((l) => l.actorUid === filters.actorUid);
  }
  if (filters.action) {
    logs = logs.filter((l) => l.action === filters.action);
  }
  if (filters.fromDate) {
    const from = new Date(filters.fromDate);
    logs = logs.filter((l) => l.createdAt >= from);
  }
  if (filters.toDate) {
    const to = new Date(filters.toDate);
    to.setHours(23, 59, 59, 999);
    logs = logs.filter((l) => l.createdAt <= to);
  }
  if (filters.search?.trim()) {
    const term = filters.search.trim().toLowerCase();
    logs = logs.filter(
      (l) =>
        l.entityId.toLowerCase().includes(term) ||
        (l.actorEmail?.toLowerCase().includes(term) ?? false) ||
        (l.actorUid.toLowerCase().includes(term) ?? false) ||
        (l.metadata &&
          JSON.stringify(l.metadata).toLowerCase().includes(term)),
    );
  }

  return logs;
}
